/**
 * Shared UI component for UI Toast.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { Component, computed, effect, input, output } from '@angular/core';

import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { UiToastType } from './ui-toast.types';

/**
 * COMPONENT: Ui Toast
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-toast',
  standalone: true,
  imports: [UiLabelTranslatePipe],
  templateUrl: './ui-toast.component.html',
  styleUrl: './ui-toast.component.scss'
})
export class UiToastComponent {
  readonly type = input<UiToastType>('info');
  /** PARENT-CHILD CONTRACT: title documents the input state boundary used by this file. */
  readonly title = input('');
  /** PARENT-CHILD CONTRACT: message documents the input state boundary used by this file. */
  readonly message = input('');
  /** PARENT-CHILD CONTRACT: visible documents the input state boundary used by this file. */
  readonly visible = input(false);
  /** PARENT-CHILD CONTRACT: durationMs documents the input state boundary used by this file. */
  readonly durationMs = input(3000);
  readonly dismiss = output<void>();

  /**
   * BUSINESS LOGIC: constructor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  constructor() {
    /** STATE OWNER: reactiveEffect documents the effect state boundary used by this file. */
    effect((onCleanup) => {
      if (!this.visible()) {
        return;
      }

      const timeout = setTimeout(() => {
        this.dismiss.emit();
      }, this.durationMs());

      onCleanup(() => clearTimeout(timeout));
    });
  }

  /** STATE OWNER: iconSymbol documents the computed state boundary used by this file. */
  readonly iconSymbol = computed(() => {
    if (this.type() === 'success') {
      return 'done';
    }

    if (this.type() === 'warning') {
      return 'warning';
    }

    if (this.type() === 'error') {
      return 'error';
    }

    return 'info';
  });

  /** STATE OWNER: ariaRole documents the computed state boundary used by this file. */
  readonly ariaRole = computed(() => (this.type() === 'error' ? 'alert' : 'status'));
}
